import React, { useMemo, useState, useCallback } from 'react';
import {
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  DoneAll as ReadIcon,
  VolumeUp,
  VolumeOff,
  Info as InfoIcon
} from '@mui/icons-material';
import NotificationErrorBoundary from './NotificationErrorBoundary';
import NotificationItem from './NotificationItem';
import NotificationBadge from './NotificationBadge';
import { useNotificationMenu } from '../../hooks/useNotificationMenu';
import { notificationSound } from '../../services/NotificationSound';
import { Notification } from '../../types/notification';

/**
 * Check if a notification is a verification rejection that should show a detail dialog
 */
const isRejectionNotification = (notification: Notification): boolean => {
  return notification.message?.includes('was not approved') === true;
};

interface NotificationMenuProps {}

const NotificationMenu: React.FC<NotificationMenuProps> = () => {
  const {
    anchorEl,
    isLoading,
    notifications,
    unreadCount,
    clearNotification,
    handleOpenMenu,
    handleCloseMenu,
    handleNotificationClick,
    handleMarkAllAsRead,
    handleViewAll
  } = useNotificationMenu();
  
  // Detail dialog state for rejection notifications
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);

  const handleItemClick = useCallback((notification: Notification) => {
    if (isRejectionNotification(notification)) {
      // Show full message in dialog instead of navigating
      setDetailNotification(notification);
      handleCloseMenu();
      // Still mark as read
      handleNotificationClick({ ...notification, actionUrl: undefined, link: undefined } as any);
    } else {
      handleNotificationClick(notification);
    }
  }, [handleNotificationClick, handleCloseMenu]);

  // Sound toggle state
  const [soundEnabled, setSoundEnabled] = React.useState(notificationSound.isEnabled());

  // Sound toggle handler
  const handleSoundToggle = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    notificationSound.toggle(newState);
  };
  
  // Memoize the notifications slice for better performance
  const visibleNotifications = useMemo(() => 
    notifications.slice(0, 5), 
    [notifications]
  );

  return (
    <NotificationErrorBoundary>
      <NotificationBadge
        count={unreadCount}
        onClick={handleOpenMenu}
        showAnimation={true}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            width: { xs: 320, sm: 380 },
            maxWidth: '95vw',
            maxHeight: { xs: 'calc(100vh - 100px)', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            p: 0
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6">
              Notifications
              {isLoading && (
                <CircularProgress 
                  size={16} 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
            <Tooltip title={`${soundEnabled ? 'Disable' : 'Enable'} notification sound`}>
              <IconButton
                size="small"
                onClick={handleSoundToggle}
                sx={{ ml: 1 }}
                aria-label="Toggle notification sound"
              >
                {soundEnabled ? <VolumeUp /> : <VolumeOff />}
              </IconButton>
            </Tooltip>
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<ReadIcon />}
              onClick={handleMarkAllAsRead}
              disabled={isLoading}
              aria-label={`Mark ${unreadCount} notifications as read`}
            >
              Mark all as read
            </Button>
          )}
        </Box>

        <Divider />

        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">No notifications</Typography>
          </Box>
        ) : (
          <>
            {/* Scrollable notification list */}
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0,
              WebkitOverflowScrolling: 'touch'
            }}>
              {visibleNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onNotificationClick={handleItemClick}
                  onDelete={clearNotification}
                  isLoading={isLoading}
                />
              ))}
            </Box>

            {/* Fixed "View All" button at bottom */}
            <Divider />
            <Box sx={{ 
              p: 1, 
              display: 'flex', 
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Button
                size="small"
                onClick={handleViewAll}
                fullWidth
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>

      {/* Rejection detail dialog */}
      <Dialog
        open={!!detailNotification}
        onClose={() => setDetailNotification(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="warning" />
          Verification Update
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {detailNotification?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDetailNotification(null)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </NotificationErrorBoundary>
  );
};

// Memoization comparison function
function areEqual(prevProps: NotificationMenuProps, nextProps: NotificationMenuProps) {
  // Component has no props, so it only needs to re-render when its hooks' values change
  return true;
}

export default React.memo(NotificationMenu, areEqual);
